import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { createDrawerRequest, drawerGeneratedName } from '@kreart/shared';
import type {
  CatalogItemDetails,
  DrawerNameMode,
  DrawerSystemVariantListItem,
  CreateCatalogItemRequest,
  CreateDrawerFromCatalogRequest,
  CreateDrawerRequest,
  DrawerListItem,
  MaterialListItem,
  PreviewDrawerRequest,
  UpdateDrawerRequest,
} from '@kreart/shared';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  Form,
  TextField,
  TextareaField,
  FormErrorBanner,
  useFieldError,
  useFormContext,
} from '@/components/form';
import { positiveIntegerFieldProps } from '@/components/form/numberInputProps';
import { CatalogItemPicker } from '@/features/project-items/shared/CatalogItemPicker';
import { useSaveCatalogBlueprint } from '@/features/project-items/shared/useSaveCatalogBlueprint';
import { useCatalogItem } from '@/features/catalog/hooks';
import {
  EdgeAutosuggest,
  MaterialAutosuggest,
} from '@/features/project-items/shared/pickers/CatalogAutosuggest';
import {
  useCatalogCoefficientPrompt,
  useCoefficientTarget,
} from '@/features/catalog/components/SaveCatalogCoefficientPrompt';
import { CoefficientField } from '@/features/project-items/shared/CoefficientField';
import { DrawerSystemSelector } from '@/features/project-items/shared/pickers/DrawerSystemSelector';
import { BreakdownPanel } from '@/features/pricing/components/BreakdownPanel';
import { usePreviewDrawer } from '@/features/pricing/hooks';
import { useDebouncedValue } from '@/lib/useDebouncedValue';
import { cn } from '@/lib/cn';
import { edgesApi } from '@/features/catalog/components/admin/api';
import { chooseEdgeForMaterial } from '@/features/project-items/shared/edgeAutoSelect';
import { useDefaultMaterialId } from '@/features/project-items/shared/useDefaultMaterial';
import { DrawerHboxField } from '@/features/project-items/shared/DrawerHboxField';
import { useCreateDrawer, useCreateDrawerFromCatalog, useUpdateDrawer } from '../hooks';
import { errorKeyFor } from '../errorMessages';

interface Props {
  projectId: string;
  cabinetId?: string;
  /**
   * Where this drawer will sit once saved, used only to preview a generated
   * name. The server is the authority and recomputes on save; omit it and the
   * field explains itself instead of guessing a position.
   */
  stackPosition?: { index: number; total: number };
  drawer?: DrawerListItem | null;
  draft?: CreateDrawerRequest | null;
  open: boolean;
  onClose: () => void;
  onSaveDraft?: (body: CreateDrawerRequest) => void | Promise<void>;
}

const positiveInteger = z.string().regex(/^[1-9]\d*$/, 'Must be a positive integer');
const positiveDecimal = z
  .string()
  .regex(/^\d+(\.\d+)?$/, 'Must be a positive number')
  .refine((v) => parseFloat(v) > 0, 'Must be greater than 0');

const formSchema = z.object({
  nameMode: z.enum(['MANUAL', 'POSITION', 'NUMBER']),
  // Optional: a drawer identified by its system and dimensions does not need a
  // name, and the lists fall back to "Sertar". The generated modes fill it in
  // server-side. Only saving a catalog blueprint insists on one.
  name: z.string().trim().max(200),
  heightMm: positiveInteger,
  widthMm: positiveInteger,
  depthMm: positiveInteger,
  materialId: z.string().min(1, 'Required'),
  frontMaterialId: z.string(),
  frontEdgeId: z.string(),
  carcassMaterial: z.string().optional(),
  frontMaterial: z.string().optional(),
  // Descriptive fields, filled in from whichever variant is chosen rather than
  // typed. They are what gets persisted on the drawer and drives pricing, but
  // the user never has to satisfy them to find a system.
  modelSertar: z.string().min(1, 'Required'),
  tipSertar: z.string().optional(),
  descriere: z.string().optional(),
  detaliu: z.string().optional(),
  color: z.string().optional(),
  // Box height in mm. Comes from the variant for Box systems (N/M/K/C); typed
  // by hand for Glisare, where the catalog has no H-box.
  hBox: z.coerce.string().min(1, 'Required'),
  drawerSystemVariantId: z.string().min(1, 'Required'),
  quantity: positiveInteger,
  coefficient: positiveDecimal,
  materialCoefficient: positiveDecimal,
  frontMaterialCoefficient: positiveDecimal,
  systemCoefficient: positiveDecimal,
  notes: z.string().trim().max(2000).optional(),
});
type FormValues = z.infer<typeof formSchema>;

const fromCatalogSchema = z.object({
  catalogItemId: z.string().min(1),
});
type FromCatalogValues = z.infer<typeof fromCatalogSchema>;
type CatalogDrawerAttributes = Omit<CreateDrawerRequest, 'quantity' | 'coefficient'>;

function integerFormValue(value: string | number | null | undefined, fallback = ''): string {
  if (value == null || value === '') return fallback;
  const parsed = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(parsed) ? String(Math.trunc(parsed)) : fallback;
}

/**
 * Never carries the default material: an existing drawer or draft keeps the
 * board it was saved with, even none. A new drawer gets the default from the
 * fill-in effect instead, so it doesn't re-seed the form when it resolves.
 */
function formValuesFromSource(
  source: Partial<CreateDrawerRequest> | DrawerListItem | null,
): FormValues {
  return {
    name: source?.name ?? '',
    nameMode: source?.nameMode ?? 'MANUAL',
    heightMm: source?.heightMm != null ? String(source.heightMm) : '',
    widthMm: source?.widthMm != null ? String(source.widthMm) : '',
    depthMm: source?.depthMm != null ? String(source.depthMm) : '',
    materialId: source?.materialId ?? '',
    frontMaterialId: source?.frontMaterialId ?? '',
    frontEdgeId: source?.frontEdgeId ?? '',
    carcassMaterial: source?.carcassMaterial ?? '',
    frontMaterial: source?.frontMaterial ?? '',
    modelSertar: source?.drawerModel ?? '',
    tipSertar: source?.drawerType ?? '',
    descriere: source?.slideModel ?? '',
    detaliu: '',
    color: source?.color ?? '',
    hBox: source?.hBox ?? '',
    drawerSystemVariantId: source?.drawerSystemVariantId ?? '',
    quantity: integerFormValue(source?.quantity, '1'),
    coefficient: source?.coefficient ?? '2.5',
    materialCoefficient: source?.materialCoefficient ?? '2.5',
    frontMaterialCoefficient: source?.frontMaterialCoefficient ?? '2.5',
    systemCoefficient: source?.systemCoefficient ?? '2.5',
    notes: source?.notes ?? '',
  };
}

function catalogDefaults(): FromCatalogValues {
  return { catalogItemId: '' };
}

function hasCatalogAttribute(item: CatalogItemDetails, key: string): boolean {
  return (
    !!item.attributes &&
    typeof item.attributes === 'object' &&
    !Array.isArray(item.attributes) &&
    Object.prototype.hasOwnProperty.call(item.attributes, key)
  );
}

function formValuesFromCatalogDrawer(item: CatalogItemDetails): FormValues {
  const parsed = createDrawerRequest.safeParse(item.attributes ?? {});
  const attrs: Partial<CreateDrawerRequest> = parsed.success ? parsed.data : {};
  const hasQuantity = hasCatalogAttribute(item, 'quantity');
  const hasCoefficient = hasCatalogAttribute(item, 'coefficient');
  return formValuesFromSource({
    name: attrs.name ?? item.name,
    heightMm: attrs.heightMm ?? item.defaultHeightMm ?? null,
    widthMm: attrs.widthMm ?? item.defaultWidthMm ?? null,
    depthMm: attrs.depthMm ?? item.defaultDepthMm ?? null,
    carcassMaterial: attrs.carcassMaterial ?? item.material ?? undefined,
    frontMaterial: attrs.frontMaterial,
    drawerModel: attrs.drawerModel,
    drawerType: attrs.drawerType,
    slideModel: attrs.slideModel,
    hBox: attrs.hBox,
    color: attrs.color,
    ...(hasQuantity && attrs.quantity !== undefined ? { quantity: attrs.quantity } : {}),
    ...(hasCoefficient && attrs.coefficient !== undefined
      ? { coefficient: attrs.coefficient }
      : {}),
    unitPriceNet: attrs.unitPriceNet ?? item.defaultCostNet ?? '0.00',
    currency: attrs.currency ?? item.currency,
    notes: attrs.notes ?? item.description ?? undefined,
    pricingMode: 'AUTO',
    materialId: attrs.materialId ?? null,
    edgeId: attrs.edgeId ?? null,
    frontMaterialId: attrs.frontMaterialId ?? null,
    frontEdgeId: attrs.frontEdgeId ?? attrs.edgeId ?? null,
    drawerSystemVariantId: attrs.drawerSystemVariantId ?? null,
    hbox: attrs.hbox ?? null,
    drawerModelKind: attrs.drawerModelKind ?? null,
    joinType: attrs.joinType ?? null,
    cx2: attrs.cx2,
  });
}

function buildDrawerCatalogAttributes(
  values: FormValues,
  unitPriceNet: string,
): CatalogDrawerAttributes {
  return {
    name: values.name,
    nameMode: values.nameMode,
    heightMm: parseInt(values.heightMm, 10),
    widthMm: parseInt(values.widthMm, 10),
    depthMm: parseInt(values.depthMm, 10),
    carcassMaterial: values.carcassMaterial || undefined,
    frontMaterial: values.frontMaterial || undefined,
    drawerModel: values.modelSertar,
    drawerType: values.tipSertar,
    slideModel: values.descriere,
    hBox: values.hBox,
    color: values.color || undefined,
    unitPriceNet,
    currency: 'RON',
    notes: values.notes || undefined,
    pricingMode: 'AUTO',
    materialId: values.materialId,
    edgeId: values.frontEdgeId || null,
    frontMaterialId: values.frontMaterialId || null,
    frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
    drawerSystemVariantId: values.drawerSystemVariantId,
    hbox: null,
    drawerModelKind: persistedModelKind(values.modelSertar),
    cx2: false,
  };
}

function buildDrawerCatalogBody(
  values: FormValues,
  unitPriceNet: string,
): CreateCatalogItemRequest {
  const drawer = buildDrawerCatalogAttributes(values, unitPriceNet);
  return {
    type: 'DRAWER',
    name: values.name,
    description: values.notes || undefined,
    defaultWidthMm: drawer.widthMm ?? null,
    defaultHeightMm: drawer.heightMm ?? null,
    defaultDepthMm: drawer.depthMm ?? null,
    material: drawer.carcassMaterial || undefined,
    color: drawer.color || undefined,
    defaultCostNet: drawer.unitPriceNet,
    currency: drawer.currency,
    attributes: drawer as Record<string, unknown>,
  };
}

function sameCatalogName(value: string, sourceName: string | undefined): boolean {
  if (!sourceName) return false;
  return value.trim().toLocaleLowerCase() === sourceName.trim().toLocaleLowerCase();
}

export function AddDrawerDialog({
  projectId,
  cabinetId,
  stackPosition,
  drawer = null,
  draft = null,
  open,
  onClose,
  onSaveDraft,
}: Props) {
  const { t } = useTranslation();
  const defaultMaterialId = useDefaultMaterialId();
  const isDraftMode = Boolean(onSaveDraft);
  const isEdit = drawer !== null || draft !== null;
  const source = drawer ?? draft;
  const sourceKey = drawer
    ? `${drawer.id}:${drawer.updatedAt}`
    : draft
      ? `draft:${draft.drawerSystemVariantId ?? ''}:${draft.name ?? ''}:${draft.quantity ?? ''}:${draft.coefficient ?? ''}`
      : 'new';
  const sourceFormValues = useMemo(() => formValuesFromSource(source), [sourceKey]);
  const [tab, setTab] = useState<'catalog' | 'manual'>('manual');
  const [sourceCatalogItemId, setSourceCatalogItemId] = useState<string | null>(null);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftError, setDraftError] = useState<unknown>(null);
  const [selectedCarcassMaterial, setSelectedCarcassMaterial] = useState<MaterialListItem | null>(
    null,
  );
  const [selectedFrontMaterial, setSelectedFrontMaterial] = useState<MaterialListItem | null>(null);

  const createMut = useCreateDrawer(projectId, cabinetId ?? '');
  const fromCatalogMut = useCreateDrawerFromCatalog(projectId, cabinetId ?? '');
  const updateMut = useUpdateDrawer(projectId, cabinetId ?? '', drawer?.id ?? '');
  const saveCatalogBlueprint = useSaveCatalogBlueprint();
  const sourceCatalogItemIdForSave = sourceCatalogItemId ?? drawer?.catalogItemId ?? null;
  const sourceCatalogItemForSave = useCatalogItem(sourceCatalogItemIdForSave || undefined);
  const mut = drawer !== null ? updateMut : sourceCatalogItemId ? fromCatalogMut : createMut;
  const isPending =
    savingDraft || saveCatalogBlueprint.isPending || (!isDraftMode && mut.isPending);

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: sourceFormValues,
  });
  const catalogForm = useForm<FromCatalogValues>({
    resolver: zodResolver(fromCatalogSchema),
    defaultValues: catalogDefaults(),
  });
  const selectedCatalogItemId = catalogForm.watch('catalogItemId');
  const selectedCatalogItemQuery = useCatalogItem(selectedCatalogItemId || undefined);
  // Set when the chosen variant carries its own H-box (Box systems). Glisare
  // variants have none, so the user types the box height instead.
  const [catalogHBox, setCatalogHBox] = useState<string | null>(
    initialCatalogHBox(sourceFormValues.hBox),
  );

  const watchedRaw = form.watch();

  const coefficientPrompt = useCatalogCoefficientPrompt();
  /**
   * The catalog rows behind the three markups on this form, so leaving one can
   * ask whether the number becomes that item's default. Resolved from the ids
   * the form holds rather than from the pickers' callbacks — a drawer opened
   * for editing, or a draft reopened from the cabinet above, never fires one.
   */
  const carcassCoefficientTarget = useCoefficientTarget(
    'material',
    watchedRaw.materialId,
    'Material Carcasa',
  );
  const frontCoefficientTarget = useCoefficientTarget(
    'material',
    watchedRaw.frontMaterialId,
    'Material Front',
  );
  const systemCoefficientTarget = useCoefficientTarget(
    'drawerSystem',
    watchedRaw.drawerSystemVariantId,
    'Sistem sertar',
  );
  const selectedEdgeMaterial = selectedFrontMaterial ?? selectedCarcassMaterial;
  const edgeSearch = selectedEdgeMaterial?.manufacturer?.trim() ?? '';
  const edgesQuery = useQuery({
    queryKey: ['catalog-edges', 'drawer-auto-select', edgeSearch],
    queryFn: () =>
      edgesApi
        .list({
          page: 1,
          pageSize: 100,
          active: true,
          includeDeleted: false,
          search: edgeSearch,
        })
        .then((res) => res.items),
    enabled:
      open &&
      Boolean(selectedEdgeMaterial) &&
      selectedEdgeMaterial?.category !== 'MDF_VOPSIT' &&
      Boolean(edgeSearch),
    meta: { silent: true },
    staleTime: 60_000,
  });

  useEffect(() => {
    if (!selectedEdgeMaterial) return;
    if (selectedEdgeMaterial.category === 'MDF_VOPSIT' || !edgeSearch) {
      form.setValue('frontEdgeId', '', { shouldDirty: true, shouldValidate: true });
      form.clearErrors('frontEdgeId');
      return;
    }
    if (!edgesQuery.data) return;
    const edge = chooseEdgeForMaterial(selectedEdgeMaterial, edgesQuery.data);
    form.setValue('frontEdgeId', edge?.id ?? '', { shouldDirty: true, shouldValidate: true });
    form.clearErrors('frontEdgeId');
  }, [edgeSearch, edgesQuery.data, form, selectedEdgeMaterial]);

  const watched = useDebouncedValue(watchedRaw, 300);

  /**
   * Copy the chosen variant's own attributes onto the form. These are the
   * values that get persisted and priced, so they always describe the product
   * that was actually selected — never the search criteria used to find it.
   */
  const applyVariant = (variant: DrawerSystemVariantListItem) => {
    const attrs = variant.attributes ?? {};
    const kind = attrs.sourceKind;
    const interior = attrs.interior === true;
    const model =
      kind === 'BOX'
        ? interior
          ? 'Box sertar interior'
          : 'Box'
        : kind === 'GLISARE'
          ? interior
            ? 'Glisare sertar interior'
            : 'Glisare'
          : '';
    const set = (fieldName: keyof FormValues, value: string) =>
      form.setValue(fieldName, value, { shouldDirty: true, shouldValidate: true });
    set('modelSertar', model);
    set('tipSertar', attrs.tipSertar ?? '');
    set('descriere', attrs.descriere ?? '');
    set('detaliu', attrs.detaliu ?? '');
    set('color', attrs.color ?? '');
    setCatalogHBox(attrs.hBox ?? null);
    if (attrs.hBox) set('hBox', attrs.hBox);
    // The slide system brings its own markup, the way a board does — it is
    // bought in, so it never follows whatever the boards are marked up at.
    // Guarded, because writing an absent value here would empty a required
    // field and block Save with nothing on screen to explain it.
    if (variant.coefficient) set('systemCoefficient', variant.coefficient);
  };

  useEffect(() => {
    if (!open) return;
    setCatalogHBox(initialCatalogHBox(sourceFormValues.hBox));
    setDraftError(null);
    form.reset(sourceFormValues);
    catalogForm.reset(catalogDefaults());
    setSourceCatalogItemId(null);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
    setTab('manual');
  }, [catalogForm, form, open, sourceFormValues]);

  // Declared after the reset above so it runs second on open; also fills in
  // when the default resolves after the dialog is already open.
  useEffect(() => {
    if (!open || isEdit || !defaultMaterialId) return;
    const current = form.getValues('materialId');
    if (!current) form.setValue('materialId', defaultMaterialId);
  }, [defaultMaterialId, form, isEdit, open]);

  useEffect(() => {
    const item = selectedCatalogItemQuery.data;
    if (!item || item.type !== 'DRAWER' || sourceCatalogItemId === item.id) return;
    const next = formValuesFromCatalogDrawer(item);
    setCatalogHBox(initialCatalogHBox(next.hBox));
    form.reset(next);
    setSourceCatalogItemId(item.id);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
    setTab('manual');
  }, [form, selectedCatalogItemQuery.data, sourceCatalogItemId]);

  const previewBody: PreviewDrawerRequest | null = useMemo(() => {
    if (
      !watched.materialId ||
      !watched.drawerSystemVariantId ||
      !watched.hBox ||
      !watched.modelSertar ||
      !watched.heightMm ||
      !watched.widthMm ||
      !watched.depthMm
    ) {
      return null;
    }
    const heightMm = parseInt(watched.heightMm, 10);
    const widthMm = parseInt(watched.widthMm, 10);
    const depthMm = parseInt(watched.depthMm, 10);
    if (!heightMm || !widthMm || !depthMm) return null;
    return {
      hBox: watched.hBox as PreviewDrawerRequest['hBox'],
      heightMm,
      widthMm,
      depthMm,
      model: previewModelKind(watched.modelSertar),
      carcassMaterialId: watched.materialId,
      carcassEdgeId: watched.frontEdgeId || null,
      frontMaterialId: watched.frontMaterialId || null,
      frontEdgeId: watched.frontMaterialId ? watched.frontEdgeId || null : null,
      drawerSystemVariantId: watched.drawerSystemVariantId,
      quantity: watched.quantity || '1',
      coefficient: watched.materialCoefficient || '2.5',
      frontCoefficient: watched.frontMaterialCoefficient || '2.5',
      systemCoefficient: watched.systemCoefficient || '2.5',
    };
  }, [watched]);
  const preview = usePreviewDrawer(previewBody);
  const submitDisabled =
    isPending || (isDraftMode && (!preview.data || preview.isFetching || Boolean(preview.error)));
  const selectedDrawerSystemLabel = drawerSystemLabel(source);

  const handleClose = () => {
    if (isPending) return;
    setDraftError(null);
    form.reset();
    catalogForm.reset();
    setSourceCatalogItemId(null);
    setSelectedCarcassMaterial(null);
    setSelectedFrontMaterial(null);
    setTab('manual');
    onClose();
  };
  const tabBtn = (id: 'catalog' | 'manual', label: string) => (
    <button
      type="button"
      onClick={() => {
        if (id === 'catalog') {
          setSourceCatalogItemId(null);
          catalogForm.reset(catalogDefaults());
        }
        setTab(id);
      }}
      className={`px-3 py-1.5 text-sm rounded-md ${
        tab === id ? 'bg-foreground text-background' : 'bg-muted text-foreground hover:bg-muted/80'
      }`}
    >
      {label}
    </button>
  );

  const onSubmit = async (values: FormValues) => {
    if (isDraftMode && (!preview.data || preview.isFetching || preview.error)) {
      setDraftError(preview.error ?? new Error('Drawer price preview is not ready.'));
      return;
    }
    const body: CreateDrawerRequest = {
      // Empty means unnamed rather than named "": the server would coerce it
      // anyway, but saying so here keeps the request honest.
      name: values.name || undefined,
      nameMode: values.nameMode,
      heightMm: parseInt(values.heightMm, 10),
      widthMm: parseInt(values.widthMm, 10),
      depthMm: parseInt(values.depthMm, 10),
      carcassMaterial: values.carcassMaterial || undefined,
      frontMaterial: values.frontMaterial || undefined,
      drawerModel: values.modelSertar,
      drawerType: values.tipSertar,
      slideModel: values.descriere,
      hBox: values.hBox,
      color: values.color || undefined,
      quantity: values.quantity,
      coefficient: values.coefficient,
      materialCoefficient: values.materialCoefficient,
      frontMaterialCoefficient: values.frontMaterialCoefficient,
      systemCoefficient: values.systemCoefficient,
      unitPriceNet: preview.data?.breakdown.subtotalNet ?? source?.unitPriceNet ?? '0.00',
      currency: 'RON',
      notes: values.notes || undefined,
      pricingMode: 'AUTO',
      materialId: values.materialId,
      edgeId: values.frontEdgeId || null,
      frontMaterialId: values.frontMaterialId || null,
      frontEdgeId: values.frontMaterialId ? values.frontEdgeId || null : null,
      drawerSystemVariantId: values.drawerSystemVariantId,
      hbox: null,
      drawerModelKind: persistedModelKind(values.modelSertar),
      cx2: false,
    };
    try {
      if (onSaveDraft) {
        setDraftError(null);
        setSavingDraft(true);
        await onSaveDraft(body);
        setSavingDraft(false);
        form.reset();
        setSelectedCarcassMaterial(null);
        setSelectedFrontMaterial(null);
        onClose();
        return;
      } else if (drawer !== null) {
        await updateMut.mutateAsync(body as UpdateDrawerRequest);
      } else if (sourceCatalogItemId) {
        const catalogBody: CreateDrawerFromCatalogRequest = {
          catalogItemId: sourceCatalogItemId,
          unitPriceNet: body.unitPriceNet,
          currency: body.currency,
          quantity: body.quantity,
          coefficient: body.coefficient,
          notes: body.notes,
          overrides: {
            name: body.name,
            heightMm: body.heightMm,
            widthMm: body.widthMm,
            depthMm: body.depthMm,
            carcassMaterial: body.carcassMaterial,
            frontMaterial: body.frontMaterial,
            drawerModel: body.drawerModel,
            drawerType: body.drawerType,
            slideModel: body.slideModel,
            hBox: body.hBox,
            color: body.color,
            pricingMode: body.pricingMode,
            materialId: body.materialId ?? null,
            edgeId: body.edgeId ?? null,
            frontMaterialId: body.frontMaterialId ?? null,
            frontEdgeId: body.frontEdgeId ?? null,
            drawerSystemVariantId: body.drawerSystemVariantId ?? null,
            hbox: body.hbox ?? null,
            drawerModelKind: body.drawerModelKind ?? null,
            joinType: body.joinType ?? null,
            cx2: body.cx2,
          },
        };
        await fromCatalogMut.mutateAsync(catalogBody);
      } else {
        await createMut.mutateAsync(body);
      }
      handleClose();
    } catch (error) {
      if (onSaveDraft) setDraftError(error);
    } finally {
      setSavingDraft(false);
    }
  };

  const saveManualToCatalog = async (values: FormValues) => {
    // The drawer itself may go unnamed, but a catalog blueprint is looked up by
    // name — the API rejects an empty one, so ask for it here rather than
    // letting that surface as a raw validation error.
    if (!values.name.trim()) {
      form.setError('name', {
        type: 'validate',
        message:
          values.nameMode === 'MANUAL'
            ? 'Adauga o denumire ca sa salvezi in catalog.'
            : 'Comuta pe Manual si adauga o denumire ca sa salvezi in catalog.',
      });
      if (values.nameMode === 'MANUAL') form.setFocus('name');
      return;
    }
    if (sameCatalogName(values.name, sourceCatalogItemForSave.data?.name)) {
      form.setError('name', {
        type: 'validate',
        message: t('projectItems.actions.errorSameCatalogName'),
      });
      form.setFocus('name');
      return;
    }
    form.clearErrors('name');
    const saved = await saveCatalogBlueprint.saveCatalogBlueprint(
      buildDrawerCatalogBody(
        values,
        preview.data?.breakdown.subtotalNet ?? source?.unitPriceNet ?? '0.00',
      ),
    );
    if (saved) handleClose();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && handleClose()}>
        <DialogContent className="max-h-[90vh] w-[min(96vw,920px)] max-w-none overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {isEdit ? t('projectItems.drawers.editTitle') : t('projectItems.drawers.addTitle')}
            </DialogTitle>
            <DialogDescription>{t('projectItems.drawers.addDescription')}</DialogDescription>
          </DialogHeader>

          {!isEdit && (
            <div className="flex gap-2">
              {tabBtn('catalog', t('projectItems.drawers.fromCatalog') ?? 'Din catalog')}
              {tabBtn('manual', t('projectItems.drawers.manual') ?? 'Manual')}
            </div>
          )}

          {!isEdit && tab === 'catalog' && (
            <Form form={catalogForm} onSubmit={() => undefined} className="space-y-4">
              <CatalogItemPicker
                type="DRAWER"
                registration={catalogForm.register('catalogItemId')}
                label={t('projectItems.drawers.fields.catalogItem') ?? 'Sertar din catalog'}
                placeholder={
                  t('projectItems.drawers.fields.catalogItemPlaceholder') ??
                  'Alege un sertar din catalog'
                }
                errorMessage={catalogForm.formState.errors.catalogItemId?.message}
              />
              <p className="text-sm text-muted-foreground">
                Selecteaza un sertar din catalog, apoi modifica toate campurile inainte de salvare.
              </p>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleClose}
                  disabled={selectedCatalogItemQuery.isFetching}
                >
                  {t('common.cancel')}
                </Button>
              </DialogFooter>
            </Form>
          )}

          {(isEdit || tab === 'manual') && (
            <Form form={form} onSubmit={(values) => onSubmit(values)} className="space-y-4">
              <DrawerNameField stackPosition={stackPosition} />

              <div className="grid grid-cols-3 gap-3">
                <TextField
                  name="heightMm"
                  label="Inaltime *"
                  {...positiveIntegerFieldProps}
                  required
                />
                <TextField
                  name="widthMm"
                  label="Latime *"
                  {...positiveIntegerFieldProps}
                  required
                />
                <TextField
                  name="depthMm"
                  label="Adancime *"
                  {...positiveIntegerFieldProps}
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <MaterialAutosuggest
                  name="materialId"
                  label="Material Carcasa"
                  usageScope="GENERAL"
                  required
                  onSelectedOption={(item) => {
                    setSelectedCarcassMaterial(item);
                    form.setValue('carcassMaterial', item?.name ?? '', { shouldDirty: true });
                    if (!selectedFrontMaterial) {
                      form.setValue('frontEdgeId', '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      });
                    }
                  }}
                />
                <MaterialAutosuggest
                  name="frontMaterialId"
                  label="Material Front"
                  usageScope="GENERAL"
                  emptyOptionLabel={t('pricing.noMaterial')}
                  onSelectedOption={(item) => {
                    setSelectedFrontMaterial(item);
                    form.setValue('frontMaterial', item?.name ?? '', { shouldDirty: true });
                    form.setValue('frontEdgeId', '', {
                      shouldDirty: true,
                      shouldValidate: true,
                    });
                  }}
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <EdgeAutosuggest
                  name="frontEdgeId"
                  label="Cant Front"
                  emptyOptionLabel={t('pricing.noEdge')}
                />
                {/* A drawer uses two boards and they are marked up separately,
                  the box and the front. The bought-in slide system has its own
                  number down by the picker it is chosen with. */}
                <CoefficientField
                  name="materialCoefficient"
                  label="Coef. corp sertar"
                  target={carcassCoefficientTarget}
                  ask={coefficientPrompt.ask}
                />
                <CoefficientField
                  name="frontMaterialCoefficient"
                  label="Coef. front sertar"
                  target={frontCoefficientTarget}
                  ask={coefficientPrompt.ask}
                />
              </div>

              <DrawerSystemSelector
                name="drawerSystemVariantId"
                label="Selectare sistem"
                required
                selectedLabel={selectedDrawerSystemLabel}
                nearDepthMm={watchedRaw.depthMm ? parseInt(watchedRaw.depthMm, 10) : undefined}
                onVariantSelected={applyVariant}
              />

              {/* Directly under the picker, because it marks up what was chosen
                there and nothing else: the slide system is bought in, so the
                boards' markups have never had any business pricing it. */}
              <div className="grid grid-cols-3 gap-3">
                <CoefficientField
                  name="systemCoefficient"
                  label="Coef. sistem sertar"
                  target={systemCoefficientTarget}
                  ask={coefficientPrompt.ask}
                />
              </div>

              <div className="grid grid-cols-3 gap-3">
                {catalogHBox ? (
                  <ReadOnlyField label="H-box" value={catalogHBox} />
                ) : (
                  <DrawerHboxField heightMm={watchedRaw.heightMm} />
                )}
                <TextField name="quantity" label="Bucati" {...positiveIntegerFieldProps} required />
              </div>

              <TextareaField name="notes" label={t('projectItems.drawers.fields.notes')} rows={3} />

              <BreakdownPanel
                breakdown={preview.data?.breakdown ?? null}
                loading={preview.isFetching}
                error={preview.error}
              />

              <FormErrorBanner error={draftError ?? mut.error} resolveKey={errorKeyFor} />

              <DialogFooter>
                <Button type="button" variant="ghost" onClick={handleClose} disabled={isPending}>
                  {t('common.cancel')}
                </Button>
                {((!isEdit && sourceCatalogItemId === null) ||
                  (Boolean(sourceCatalogItemIdForSave) && form.formState.isDirty)) && (
                  <Button
                    type="button"
                    variant="outline"
                    onClick={form.handleSubmit(saveManualToCatalog)}
                    disabled={submitDisabled}
                  >
                    {saveCatalogBlueprint.isPending
                      ? t('common.loading')
                      : t('projectItems.actions.saveToCatalog')}
                  </Button>
                )}
                <Button type="submit" disabled={submitDisabled}>
                  {isPending ? t('common.loading') : t('common.submit')}
                </Button>
              </DialogFooter>
            </Form>
          )}
        </DialogContent>
      </Dialog>
      {/* A sibling, never a child. Two Radix dialogs nested share one scroll
          lock, and the inner one leaves `pointer-events: none` on <body> even
          while closed — which silently swallows every click in the dialog
          above it. */}
      {coefficientPrompt.dialog}
    </>
  );
}

const NAME_MODE_OPTIONS: Array<{ value: DrawerNameMode; label: string; hint: string }> = [
  { value: 'MANUAL', label: 'Manual', hint: 'Scrii tu denumirea.' },
  {
    value: 'POSITION',
    label: 'Pozitie',
    hint: 'Sus, Mijloc, Jos - de sus in jos, dupa locul in corp.',
  },
  { value: 'NUMBER', label: 'Numerotare', hint: 'Sertar 1, Sertar 2, ... - de sus in jos.' },
];

/**
 * The drawer's name, and how it is decided.
 *
 * The two generated modes are resolved server-side, because the name depends on
 * how many drawers end up in the cabinet and in what order — something this
 * dialog cannot know for certain while it is open. What is shown here is a
 * preview of the position the drawer is heading for; adding or removing a
 * sibling later renames it and the ones around it.
 */
function DrawerNameField({ stackPosition }: { stackPosition?: { index: number; total: number } }) {
  const form = useFormContext();
  const mode = (form.watch('nameMode') ?? 'MANUAL') as DrawerNameMode;
  // Saving to the catalog needs a name even though the drawer itself does not,
  // and in a generated mode there is no input to hang that error on.
  const nameError = useFieldError('name');
  const preview = stackPosition
    ? drawerGeneratedName(mode, stackPosition.index, stackPosition.total)
    : null;

  const modes = (
    <div className="flex flex-wrap items-center gap-1.5">
      {NAME_MODE_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          aria-pressed={mode === option.value}
          title={option.hint}
          className={cn(
            'rounded-full border px-3 py-1 text-xs transition-colors',
            mode === option.value
              ? 'border-primary bg-primary text-primary-foreground'
              : 'border-border hover:bg-accent hover:text-accent-foreground',
          )}
          onClick={() =>
            form.setValue('nameMode', option.value, {
              shouldDirty: true,
              shouldValidate: true,
            })
          }
        >
          {option.label}
        </button>
      ))}
    </div>
  );

  if (mode === 'MANUAL') {
    return <TextField name="name" label="Denumire" inputAccessory={modes} />;
  }

  return (
    <div>
      <p className="text-sm font-medium">Denumire</p>
      {/* Mirrors TextField's input row, so the switch keeps its place when the
          box is replaced by the generated preview. */}
      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 rounded-md border border-border bg-muted px-3 py-2 text-sm">
          {preview ?? 'Se completeaza automat la salvare'}
        </p>
        {modes}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {mode === 'POSITION'
          ? 'Sus, Mijloc, Jos - se renumeroteaza cand adaugi sau stergi sertare.'
          : 'Sertar 1, Sertar 2 - se renumeroteaza cand adaugi sau stergi sertare.'}
      </p>
      {nameError && (
        <p className="mt-1 text-xs text-destructive" role="alert">
          {nameError}
        </p>
      )}
    </div>
  );
}

/**
 * A read-only mirror of a value the catalog decided, shown where an input used
 * to sit so the form still reads as a complete row.
 */
function ReadOnlyField({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm font-medium">{label}</p>
      <p className="mt-1 rounded-md border border-border bg-muted px-3 py-2 text-sm">{value}</p>
    </div>
  );
}

/**
 * Box H-box codes are letters (N/M/K/C); Glisare box heights are plain
 * millimetres. A non-numeric value therefore came from the catalog and should
 * be shown read-only rather than as an editable field.
 */
function initialCatalogHBox(hBox: string | undefined): string | null {
  if (!hBox) return null;
  return Number.isFinite(Number(hBox)) ? null : hBox;
}

function previewModelKind(modelSertar: string): PreviewDrawerRequest['model'] {
  if (modelSertar === 'Box sertar interior') return 'BOX_INTERIOR';
  if (modelSertar === 'Box') return 'BOX';
  if (modelSertar === 'Glisare sertar interior') return 'INTERIOR';
  return 'STANDARD';
}

function persistedModelKind(modelSertar: string): CreateDrawerRequest['drawerModelKind'] {
  if (modelSertar.startsWith('Box')) return 'BOX';
  if (modelSertar.includes('interior')) return 'INTERIOR';
  return 'STANDARD';
}

function drawerSystemLabel(
  source: CreateDrawerRequest | DrawerListItem | null,
): string | undefined {
  if (!source?.drawerSystemVariantId) return undefined;
  const label = [
    source.drawerModel,
    source.drawerType,
    source.slideModel,
    source.hBox ? `H-box ${source.hBox}` : null,
  ]
    .filter(Boolean)
    .join(' / ');
  return label || source.drawerSystemVariantId;
}
